import { useHistory } from 'react-router-dom';
import { useEffect, useState, useContext, useRef } from 'react';
import { fetchMyCampaign } from '~/services/campaign';
import { MyCampaignResponse } from '~/types/campaign';
import { TeamSection } from './components/TeamItems';
import {
    ApplicationIncompleteSection,
    ApplicationSection,
    ApplicationStatusSection,
    MessageCard,
    DashboardNotifications,
} from './components/ApplicationItems';
import { FundRequestsAnnouncementCard } from './components/FundRequestsAnnouncementCard';
import { StatsSection } from './components/CampaignStats';
import {
    CampaignHeaderSection,
    CampaignItemsSection,
    CampaignWelcomeCard,
} from '~/shared/components/cards/CampaignItemsCard';
import { CampaignContext } from '~/context/campaign';
import { AuthContext } from '~/context/auth';
import { ProgressCard } from './components/ProgressCard';
import { Loading } from '~/shared/components/Loading';

export interface IAppProps {}

export function Dashboard(_props: IAppProps) {
    const [loading, setLoading] = useState<boolean>(true);
    const [fetchingCampaign, setFetchingCampaign] = useState<boolean>(false);
    const [{ campaign }, campaignDispatch] = useContext(CampaignContext);
    const [{ user }, userDispatch] = useContext(AuthContext);

    const mountedRef = useRef(true);
    const history = useHistory();

    useEffect(() => {
        if (campaign && mountedRef.current) {
            setLoading(false);
        }
    }, [user, campaign]);

    useEffect(() => {
        // Don't run campaign fetching logic until we know the user's authentication state
        const hasToken = !!localStorage.getItem('token');
        const userLoaded = user !== null || !hasToken; // User is loaded OR no token (not authenticated)

        if (!userLoaded) {
            console.log('📊 Waiting for user to load before fetching campaign');
            return;
        }

        if (!campaign || !campaign.campaignDetail) {
            // If user is superuser, AdminNavBar handles campaign loading via URL params
            // Dashboard should not make duplicate API calls
            if (user?.is_superuser) {
                console.log('📊 Superuser mode: AdminNavBar handles campaign loading');
                return;
            }
            // Regular user mode: fetch their own campaign
            else {
                console.log('📊 Regular user mode: Fetching own campaign');
                if (!fetchingCampaign) {
                    setFetchingCampaign(true);
                    getMyCampaign();
                }
            }
        }
        return () => {
            mountedRef.current = false;
        };
    }, [user, fetchingCampaign]);

    const getMyCampaign = async () => {
        
        try {
            const resp: MyCampaignResponse = await fetchMyCampaign(); // No campaignId for regular users
            
            if (resp.detail) {
                // Delete localStorage token
                localStorage.removeItem('token');
                campaignDispatch({
                    type: 'set_campaign',
                    payload: null,
                });
                // Preserve current URL as next parameter when redirecting to login
                const currentPath = history.location.pathname + history.location.search;
                history.push(`/login?next=${encodeURIComponent(currentPath)}`);
                return;
            } else {
                campaignDispatch({
                    type: 'set_campaign',
                    payload: resp,
                });
                const { person, role } = resp;
                userDispatch({
                    type: 'set_person',
                    person,
                    role,
                });
                setLoading(false);
            }
        } catch (e) {
            console.log('getMyCampaign() e: ', e);
            // Show some error here
        }
    };



    if (!campaign || loading) {
        return <Loading />;
    }
    const {
        campaignDetail,
        campaignStats,
        campaignFundraisers,
        campaignCoordinator,
        campaignApplicationItems,
        campaignApplicationStatus,
        campaignFundProgress,
    } = campaign;
    let hideApplication = false;
    const hideApplicationStatuses = ['Disbursable', 'Funded'];
    if (hideApplicationStatuses.includes(campaignDetail?.campaign_status)) {
        hideApplication = true;
    }

    let hideCampaignItems = false;
    const hideCampaignItemStatuses = ['UnPublished', 'Inactive', 'Unpublished'];
    if (hideCampaignItemStatuses.includes(campaignDetail?.campaign_status)) {
        hideCampaignItems = true;
    }

    const medProfItem = campaignApplicationItems?.find(
        (item: any) => item.item_type === 'medpro'
    );
    const medProfPending = medProfItem?.complete === false;

    return (
        <>
            <DashboardNotifications />
            <FundRequestsAnnouncementCard />
            {!hideApplication && (
                <MessageCard app_item={campaignApplicationStatus} />
            )}

            <div className='container__inner'>
                <CampaignHeaderSection
                    leftcard={
                        <CampaignWelcomeCard
                            detail={campaignDetail}
                            status={campaignApplicationStatus}
                        />
                    }
                    contact={campaignCoordinator}
                />
                {hideApplication && (
                    <ProgressCard
                        campaign={campaignDetail}
                        progress={campaignFundProgress}
                    />
                )}
                <ApplicationStatusSection
                    app_item={campaignApplicationStatus}
                />
                {!hideApplication && (
                    <ApplicationSection app_items={campaignApplicationItems} />
                )}
                {hideApplication && (
                    <StatsSection
                        stat_items={campaignStats}
                        medProfPending={medProfPending}
                    />
                )}
                <hr />
                {campaignDetail?.campaign_status !== 'Disbursable' && (
                    <ApplicationIncompleteSection
                        status_item={campaignApplicationStatus}
                        app_items={campaignApplicationItems}
                    />
                )}

                {!hideCampaignItems && (
                    <CampaignItemsSection
                        donations={campaignStats}
                        campaign={campaignDetail}
                        fundraisers={campaignFundraisers}
                    />
                )}

                <TeamSection members={campaignDetail?.team_members || []} />
            </div>
        </>
    );
}
